/**
 * Which locale a reader gets (docs/contributing/i18n.md, "Negotiation"). The dashboard (tier A): the user's setting,
 * else the browser's languages (what Accept-Language carries), else the company default, else English. The widget and
 * the homeowner's documents (tier B) have their own rules below. The same rules and the same shared
 * cases (i18n/spec/negotiation-cases.json) as the backend (app/design/i18n/negotiate.py) and the widget (js/i18n.js).
 */
import { LOCALES, PSEUDO_LOCALES, SOURCE_LOCALE, type LocaleInfo } from "./locales.gen.ts";

export type Tier = "A" | "B";

const TRADITIONAL_CHINESE = new Set(["zh-tw", "zh-hk", "zh-mo", "zh-hant"]);
const norm = (tag: string) => tag.trim().replace(/_/g, "-").toLowerCase();
const language = (tag: string) => norm(tag).split("-")[0]!;
const ALL: LocaleInfo[] = [...LOCALES, ...PSEUDO_LOCALES];

/** Tier A locales: the app (dashboard, admin, landing, Help Centre). */
export const APP_LOCALES: LocaleInfo[] = LOCALES.filter((l) => l.tiers.includes("A"));
/** Tier B locales: what homeowners see (widget, quote page, emails, proposal). */
export const HOMEOWNER_LOCALES: LocaleInfo[] = LOCALES.filter((l) => l.tiers.includes("B"));

/** `code` if it is in `tier`, else the first of its fallbacks that is (fr-CA → fr, pt-PT → pt-BR), never English for
 * another language: a Swedish reader of the dashboard gets the next rule (browser, company), not English. */
function inTier(code: string, tier: Tier): string | null {
  const info = ALL.find((l) => l.code === code);
  if (!info) return null;
  if (info.tiers.includes(tier)) return code;
  for (const f of info.fallback) {
    if (f === SOURCE_LOCALE && language(code) !== language(SOURCE_LOCALE)) return null;
    const next = ALL.find((l) => l.code === f);
    if (next?.tiers.includes(tier)) return f;
  }
  return null;
}

/** The supported locale of `tier` that `tag` names (exactly, by alias, or by language), or null. Tier B (the
 * default) holds every locale; tier A narrows to the app's 21 through the fallback chain. */
export function match(tag: string | null | undefined, allowPseudo = false, tier: Tier = "B"): string | null {
  if (!tag || typeof tag !== "string") return null;
  const wanted = norm(tag);
  if (!wanted || wanted.length > 35) return null;
  // a pseudo-locale where they are off is no language at all (never quietly English)
  if (!allowPseudo && PSEUDO_LOCALES.some((p) => norm(p.code) === wanted)) return null;
  const infos: LocaleInfo[] = allowPseudo ? ALL : LOCALES;
  for (const info of infos) if (norm(info.code) === wanted) return inTier(info.code, tier);
  for (const info of infos) if ((info.aliases ?? []).some((a) => norm(a) === wanted)) return inTier(info.code, tier);
  // Traditional Chinese is its own locale, never Simplified
  if (TRADITIONAL_CHINESE.has(wanted) || wanted.startsWith("zh-hant")) return inTier("zh-Hant", tier);
  for (const info of LOCALES) if (language(info.code) === language(wanted)) return inTier(info.code, tier);
  return null;
}

/** The tags of an Accept-Language header, most wanted first (q=0 and * left out). */
export function parseAcceptLanguage(header: string | null | undefined): string[] {
  if (!header) return [];
  const ranked: [number, number, string][] = [];
  header.split(",").slice(0, 30).forEach((part, i) => {
    const [tag, ...params] = part.trim().split(";");
    let q = 1;
    for (const p of params) {
      const [k, v] = p.trim().split("=");
      if (k === "q") q = Number.isFinite(Number(v)) ? Number(v) : 0;
    }
    if (tag && tag.trim() !== "*" && q > 0) ranked.push([-q, i, tag.trim()]);
  });
  return ranked.sort((a, b) => a[0] - b[0] || a[1] - b[1]).map((r) => r[2]);
}

export function firstMatch(tags: readonly (string | null | undefined)[], allowPseudo = false, tier: Tier = "B"): string | null {
  for (const tag of tags) {
    const found = match(tag, allowPseudo, tier);
    if (found) return found;
  }
  return null;
}

export function forDashboard(user: string | null | undefined, browser: readonly string[], company: string | null | undefined, allowPseudo = false): string {
  return match(user, allowPseudo, "A") ?? firstMatch(browser, allowPseudo, "A") ?? match(company, false, "A") ?? SOURCE_LOCALE;
}

/** The dashboard before anyone is signed in (sign-in, sign-up): `?locale=` (the landing site's links carry its
 * language), else the language chosen last on this browser, else the browser's languages, else English. Pseudo-locales
 * only where `allowPseudo` (development and tests). Once a user is known, `forDashboard` decides. */
export function forSignedOut(param: string | null | undefined, stored: string | null | undefined, browser: readonly string[], allowPseudo = false): string {
  return match(param, allowPseudo, "A") ?? match(stored, allowPseudo, "A") ?? forDashboard(null, browser, null, allowPseudo);
}

export function forWidget(embed: string | null | undefined, browser: readonly string[], company: string | null | undefined, allowPseudo = false): string {
  return match(embed, allowPseudo, "B") ?? firstMatch(browser, allowPseudo, "B") ?? match(company, false, "B") ?? SOURCE_LOCALE;
}

export function forHomeowner(lead: string | null | undefined, company: string | null | undefined, allowPseudo = false): string {
  return match(lead, allowPseudo, "B") ?? match(company, false, "B") ?? SOURCE_LOCALE;
}

/** Where a message is looked for: the locale, its fallbacks (pt-PT → pt-BR → en), then the source. */
export function chain(code: string): string[] {
  const info = ALL.find((l) => l.code === code);
  const out = [code, ...(info?.fallback ?? [])];
  if (!out.includes(SOURCE_LOCALE)) out.push(SOURCE_LOCALE);
  return out;
}

export function localeInfo(code: string): LocaleInfo {
  return ALL.find((l) => l.code === code) ?? LOCALES[0]!;
}

/** The locale Intl formats with: Spanish as Latin American Spanish, the Gregorian calendar everywhere (Thai and
 * Persian would otherwise show the Buddhist and Solar Hijri years), pseudo-locales as English. Shared policy with the
 * backend's formatting.py. */
export function formatLocale(code: string): string {
  if (code === "en-XA" || code === "ar-XB") return "en";
  if (code === "es") return "es-419";
  if (code === "th" || code === "fa") return `${code}-u-ca-gregory`;
  return code;
}
